// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClock } from "@fortawesome/free-solid-svg-icons";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import Seo from "../Seo/SeoComponent";
import BlogSubscribePrompt from "../NewsletterForm/BlogSubscribePrompt";
import { blogPosts } from "../../consts/blogPosts";
import { blogPostContentPt } from "../../consts/blogPostsPt";
import { estimateReadingMinutes } from "../../utils/blogReadingTime";
import { joyoKanji } from "../../consts/joyoKanji";
import { totalKanjiCount } from "../../consts/allKanjiMeanings";
import { useAuth } from "../../context/AuthContext";
import { useBlogReadProgress } from "../../hooks/useBlogReadProgress";
import { LearnedBadge } from "../LearningStatus/LearningStatus";
import styles from "./blogPage.module.scss";

function formatDate(dateString, locale = "en-US") {
    return new Date(`${dateString}T00:00:00`).toLocaleDateString(locale, {
        year: "numeric",
        month: "long",
        day: "numeric",
    });
}

// Lowercases and strips accents so "cafe" finds "café" and PT titles match without diacritics.
function normalizeSearchText(value) {
    return String(value ?? "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase();
}

// Chips shown before "Show all topics"; keeps the collapsed row to about two lines on a phone.
const COLLAPSED_TAG_COUNT = 6;

function BlogPage() {
    const { t, i18n } = useTranslation("blogPage");
    const [query, setQuery] = useState("");
    const { status: authStatus } = useAuth();
    const { progress: readProgress } = useBlogReadProgress();
    const [searchParams, setSearchParams] = useSearchParams();
    const isPt = i18n.language === "pt";
    const currentPath = isPt ? "/pt/blog" : "/blog";
    const sortedPosts = [...blogPosts].sort((a, b) => (a.date < b.date ? 1 : -1));
    const counts = {
        joyoCount: joyoKanji.length.toLocaleString("pt-PT"),
        totalCount: totalKanjiCount.toLocaleString("pt-PT"),
    };
    // Tags are kept as their canonical (English) key here - each post card translates
    // its own tags for display, so the same key can drive the `?tag=` filter link below.
    // `keywords` stays the canonical (English) list so it can drive the `?keyword=` filter;
    // `keywordLabels` is the same list in the display language, index for index.
    const posts = isPt
        ? sortedPosts
              .filter((post) =>
                  post.lang === "pt-PT"
                      ? Array.isArray(post.content)
                      : !post.ptSlug && Array.isArray(blogPostContentPt[post.slug]) && blogPostContentPt[post.slug].length > 0
              )
              .map((post) => {
                  if (post.lang === "pt-PT") {
                      return {
                          ...post,
                          path: `/blog/${post.slug}`,
                          keywordLabels: post.keywords,
                          readingMinutes: estimateReadingMinutes(post.content),
                      };
                  }
                  const translatedKeywords = t(`posts.${post.slug}.keywords`, {
                      returnObjects: true,
                      defaultValue: post.keywords,
                  });
                  return {
                      ...post,
                      lang: "pt-PT",
                      path: `/pt/blog/${post.slug}`,
                      title: t(`posts.${post.slug}.title`, { defaultValue: post.title, ...counts }),
                      excerpt: t(`posts.${post.slug}.excerpt`, { defaultValue: post.excerpt, ...counts }),
                      keywordLabels: Array.isArray(translatedKeywords) ? translatedKeywords : post.keywords,
                      readingMinutes: estimateReadingMinutes(blogPostContentPt[post.slug] ?? post.content),
                  };
              })
        : sortedPosts.filter((post) => post.lang !== "pt-PT").map((post) => ({
              ...post,
              path: `/blog/${post.slug}`,
              keywordLabels: post.keywords,
              readingMinutes: estimateReadingMinutes(post.content),
          }));

    const activeTag = searchParams.get("tag");
    const activeKeyword = searchParams.get("keyword");
    const activeKeywordLabel = activeKeyword
        ? posts.map((post) => post.keywordLabels?.[post.keywords?.indexOf(activeKeyword)]).find(Boolean) ?? activeKeyword
        : null;
    // One chip per category that has posts in this language, busiest first (ties by display name).
    const tagCounts = new Map();
    posts.forEach((post) => (post.tags ?? []).forEach((tag) => tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1)));
    const tagOptions = [...tagCounts.entries()]
        .map(([tag, count]) => ({ tag, count, label: t(`tags.${tag}`, { defaultValue: tag }) }))
        .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, isPt ? "pt-PT" : "en"));
    const selectedTagHidden = Boolean(activeTag) && tagOptions.findIndex((o) => o.tag === activeTag) >= COLLAPSED_TAG_COUNT;
    const [tagsExpanded, setTagsExpanded] = useState(selectedTagHidden);
    // A selection in the hidden part (shared URL, Back button) must never be out of sight.
    useEffect(() => {
        if (selectedTagHidden) setTagsExpanded(true);
    }, [selectedTagHidden]);
    const shownTagOptions = tagsExpanded ? tagOptions : tagOptions.slice(0, COLLAPSED_TAG_COUNT);
    // Chips drive the same `?tag=` param as each card's tag link; only the tag changes, so a keyword filter survives.
    const selectTag = (tag) => {
        const next = new URLSearchParams(searchParams);
        if (tag) next.set("tag", tag);
        else next.delete("tag");
        setSearchParams(next);
    };
    // A `?tag=` that matches a chip is already shown (and cleared) by the chips; the banner covers the rest.
    const showFilterBanner = Boolean(activeKeyword) || (Boolean(activeTag) && !tagCounts.has(activeTag));
    const searchTerms = normalizeSearchText(query).trim().split(/\s+/).filter(Boolean);
    const matchesSearch = (post) => {
        if (!searchTerms.length) return true;
        const haystack = normalizeSearchText(
            [
                post.title,
                post.excerpt,
                ...(post.keywordLabels ?? []),
                ...(post.tags ?? []).map((tag) => t(`tags.${tag}`, { defaultValue: tag })),
            ].join(" ")
        );
        return searchTerms.every((term) => haystack.includes(term));
    };
    const visiblePosts = posts.filter(
        (post) =>
            (!activeTag || post.tags?.includes(activeTag)) &&
            (!activeKeyword || post.keywords?.includes(activeKeyword)) &&
            matchesSearch(post)
    );

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />

            <main className={styles.main}>
                <section className={styles.container}>
                    <h1 className={styles.title}>{t("title")}</h1>
                    <p className={styles.description}>{t("description")}</p>
                    <a href="/rss.xml" className={styles.rssLink}>
                        {t("rssFeed")}
                    </a>

                    <div className={styles.postSearch} role="search">
                        <label htmlFor="blog-post-search" className={styles.postSearchLabel}>
                            {t("postSearch.label")}
                        </label>
                        <div className={styles.postSearchField}>
                            <input
                                id="blog-post-search"
                                type="search"
                                className={styles.postSearchInput}
                                value={query}
                                onChange={(event) => setQuery(event.target.value)}
                                placeholder={t("postSearch.placeholder")}
                                autoComplete="off"
                            />
                            {query && (
                                <button
                                    type="button"
                                    className={styles.postSearchClear}
                                    onClick={() => setQuery("")}
                                >
                                    {t("postSearch.clear")}
                                </button>
                            )}
                        </div>
                    </div>

                    {tagOptions.length > 0 && (
                        <div id="blog-category-filter" className={styles.categoryFilter} role="group" aria-label={t("categoryFilter.label")}>
                            {[{ tag: null, count: posts.length, label: t("categoryFilter.all") }, ...shownTagOptions].map(
                                ({ tag, count, label }) => {
                                    const selected = (activeTag ?? null) === tag;
                                    return (
                                        <button
                                            key={tag ?? "__all"}
                                            type="button"
                                            className={`${styles.categoryChip} ${selected ? styles.categoryChipSelected : ""}`}
                                            aria-pressed={selected}
                                            onClick={() => selectTag(tag)}
                                        >
                                            {`${label} (${count})`}
                                        </button>
                                    );
                                }
                            )}
                            {tagOptions.length > COLLAPSED_TAG_COUNT && (
                                <button
                                    type="button"
                                    className={`${styles.categoryChip} ${styles.categoryToggle}`}
                                    aria-expanded={tagsExpanded}
                                    aria-controls="blog-category-filter"
                                    onClick={() => setTagsExpanded(!tagsExpanded)}
                                >
                                    {tagsExpanded ? t("categoryFilter.showFewer") : t("categoryFilter.showAll")}
                                </button>
                            )}
                        </div>
                    )}

                    {showFilterBanner && (
                        <div className={styles.activeFilter}>
                            <span>
                                {t("post.filteringBy", {
                                    tag: activeKeyword
                                        ? `#${activeKeywordLabel}`
                                        : t(`tags.${activeTag}`, { defaultValue: activeTag }),
                                })}
                            </span>
                            <Link to={currentPath} className={styles.clearFilter}>
                                {t("post.clearFilter")}
                            </Link>
                        </div>
                    )}

                    <div className={styles.postList}>
                        {visiblePosts.map((post) => (
                            <article key={post.slug} className={styles.postCard}>
                                <div className={styles.postMeta}>
                                    <time dateTime={post.date}>{formatDate(post.date, isPt ? "pt-PT" : "en-US")}</time>
                                    <span className={styles.postReadingTime}>
                                        <FontAwesomeIcon
                                            icon={faClock}
                                            aria-hidden="true"
                                            className={styles.readingTimeIcon}
                                        />
                                        {t("post.readingTime", { count: post.readingMinutes })}
                                    </span>
                                    {authStatus === "authenticated" && readProgress[post.slug] === "read" && (
                                        <LearnedBadge label={t("post.readBadge")} />
                                    )}
                                    {post.tags?.map((tag) => (
                                        <Link
                                            key={tag}
                                            to={`${currentPath}?tag=${encodeURIComponent(tag)}`}
                                            className={styles.postTag}
                                            // nofollow: these filter links are for readers, not crawlers. There
                                            // are ~22 tags and ~500 keywords per language, so following them
                                            // gives Google ~1,100 URLs that all render the same list and
                                            // canonicalise back to it - crawl budget spent on nothing. See NIH-312.
                                            rel="nofollow"
                                        >
                                            {t(`tags.${tag}`, { defaultValue: tag })}
                                        </Link>
                                    ))}
                                </div>
                                <h2 className={styles.postTitle}>
                                    <Link to={post.path} className={styles.postCardLink} lang={post.lang}>
                                        {post.title}
                                    </Link>
                                </h2>
                                <p className={styles.postExcerpt}>{post.excerpt}</p>
                                {post.keywords?.length > 0 && (
                                    <div className={styles.keywordRow}>
                                        {post.keywords.map((keyword, index) => (
                                            <Link
                                                key={keyword}
                                                to={`${currentPath}?keyword=${encodeURIComponent(keyword)}`}
                                                className={styles.keyword}
                                                rel="nofollow"
                                            >
                                                #{post.keywordLabels?.[index] ?? keyword}
                                            </Link>
                                        ))}
                                    </div>
                                )}
                                <span className={styles.readMore}>{t("readMore")}</span>
                            </article>
                        ))}
                        {(activeTag || activeKeyword) && !visiblePosts.length && (
                            <p className={styles.noResults}>{t("post.noTaggedPosts")}</p>
                        )}
                        {query.trim() && !visiblePosts.length && (
                            <p className={styles.noResults}>{t("postSearch.noResults", { query: query.trim() })}</p>
                        )}
                    </div>

                    <BlogSubscribePrompt />
                </section>
            </main>

            <Footer />
        </div>
    );
}

export default BlogPage;
